import { Mail, Phone, MapPin, Building2 } from "lucide-react";
import { Link } from "react-router-dom";

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  const workingLinks = [
    { label: "Our Industries", href: "#industries" },
    { label: "Why IQ Hub", href: "#why-iqhub" },
    { label: "Get Involved", href: "#join" },
    { label: "FAQ", href: "#faq" },
    { label: "Let's Connect", href: "#contact" },
  ];

  const legalLinks = [
    { label: "Terms & Conditions", to: "/terms-and-conditions" },
    { label: "Privacy Policy", to: "/privacy-policy" },
    { label: "Cookie Policy", to: "/cookie-policy" },
  ];

  return (
    <footer className="bg-[#0A0F1E] text-[#94A3B8]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-16 pb-8">
        {/* Main Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 pb-12 border-b border-white/5">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-5">
              <img
                src="/assets/logo-for-dark-bg.png"
                alt="IQ Hub"
                className="h-10 w-auto"
              />
            </div>
            <p className="text-sm leading-relaxed text-[#94A3B8] font-medium mb-2 max-w-xs">
              IQ International Pte Ltd
            </p>
            <p className="text-sm leading-relaxed text-[#64748B] mb-6 max-w-xs">
              Premium knowledge networking company organizing world-class conferences across
              8 industries worldwide.
            </p>

            {/* Office Addresses */}
            <div className="space-y-4">
              <div>
                <div className="flex items-center gap-2 mb-1.5">
                  <Building2 className="w-3.5 h-3.5 text-[#C8A951]" />
                  <span className="text-xs font-semibold text-[#C8A951] tracking-wider uppercase">
                    U.S. Office
                  </span>
                </div>
                <p className="text-xs text-[#64748B] leading-relaxed pl-[22px]">
                  IQ Square, 1120-113 Cherry Street,
                  <br />
                  Seattle, Washington, 98104
                </p>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1.5">
                  <Building2 className="w-3.5 h-3.5 text-[#C8A951]" />
                  <span className="text-xs font-semibold text-[#C8A951] tracking-wider uppercase">
                    Head Office
                  </span>
                </div>
                <p className="text-xs text-[#64748B] leading-relaxed pl-[22px]">
                  8 Marina View, #07-04 Asia Square Tower 1,
                  <br />
                  Singapore, 018960
                </p>
              </div>
            </div>
          </div>

          {/* Quick Links — only working ones */}
          <div>
            <h4 className="text-white text-sm font-semibold mb-5 tracking-wide">Quick Links</h4>
            <ul className="space-y-3">
              {workingLinks.map((link, i) => (
                <li key={i}>
                  <a
                    href={link.href}
                    className="text-sm text-[#64748B] hover:text-[#C8A951] transition-colors"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
            
            {/* Legal Links */}
            <h4 className="text-white text-sm font-semibold mb-4 mt-8 tracking-wide">Legal</h4>
            <ul className="space-y-3">
              {legalLinks.map((link, i) => (
                <li key={i}>
                  <Link
                    to={link.to}
                    className="text-sm text-[#64748B] hover:text-[#C8A951] transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white text-sm font-semibold mb-5 tracking-wide">Contact</h4>
            <ul className="space-y-4">
              <li className="flex items-center gap-2 text-sm">
                <Mail className="w-4 h-4 text-[#475569] shrink-0" />
                <a href="mailto:support@iq-hub.com" className="hover:text-[#C8A951] transition-colors">
                  support@iq-hub.com
                </a>
              </li>
              <li>
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="w-4 h-4 text-[#475569] shrink-0" />
                  <a href="tel:+12065820128" className="hover:text-[#C8A951] transition-colors">
                    +1 206-582-0128
                  </a>
                </div>
                <span className="text-xs text-[#475569] ml-6">U.S.</span>
              </li>
              <li>
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="w-4 h-4 text-[#475569] shrink-0" />
                  <a href="tel:+6531584211" className="hover:text-[#C8A951] transition-colors">
                    +65 3158-4211
                  </a>
                </div>
                <span className="text-xs text-[#475569] ml-6">Singapore</span>
              </li>
              <li className="flex items-start gap-2 text-sm">
                <MapPin className="w-4 h-4 text-[#475569] mt-0.5 shrink-0" />
                <span>Seattle, WA · Singapore · London, UK · Dubai, UAE</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar — clean, no dead links */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-8">
          <p className="text-xs text-[#475569]">
            © {currentYear} IQ International Pte Ltd. All rights reserved.
          </p>
          <div className="flex items-center gap-4 text-xs text-[#475569]">
            <Link to="/terms-and-conditions" className="hover:text-[#C8A951] transition-colors">Terms</Link>
            <span>·</span>
            <Link to="/privacy-policy" className="hover:text-[#C8A951] transition-colors">Privacy</Link>
            <span>·</span>
            <Link to="/cookie-policy" className="hover:text-[#C8A951] transition-colors">Cookies</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
